package com.siriusxm.cmc

import androidx.compose.runtime.*
import com.siriusxm.cmc.utils.logInfo
import com.siriusxm.pia.AppStylesheet
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.*
import com.siriusxm.pia.views.accesscontrols.AccessControlBadgeStyles
import kotlinx.browser.document
import kotlinx.browser.localStorage
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.CustomEvent
import org.w3c.dom.CustomEventInit
import org.w3c.dom.StorageEvent
import org.w3c.dom.events.Event

/**
 * Defines the main application view configuration
 */
data class ApplicationViewConfig(
    val theme: String
)

const val DARK_THEME = "dark"
const val LIGHT_THEME = "light"

private const val THEME_CHANGED_EVENT = "theme-changed"

private const val STORAGE_EVENT = "storage"

private const val THEME_STORAGE_KEY = "theme"

/**
 * Renders a default page with default styles, useful for development of embedded applications to mimic
 * the environment of CMC.
 *
 * By default the view is sized to exactly the viewport, so the page itself never scrolls and each
 * application's own scroll containers are the only ones. Standalone and embedded applications use
 * this: neither draws a header of its own, and an embedded application's iframe already starts below
 * the console header and ends at the audio player, so the whole viewport belongs to the application.
 *
 * With [fillParent], the view instead takes whatever height is left in a flex-column parent. The
 * console uses this to stack header, content and the global audio player, so the content area (and
 * every scroll area inside it, including an embedded application's iframe) shrinks to make room for
 * the player without the application having to know the player exists. Content taller than the view
 * scrolls inside it, so the header and player stay put.
 */
@Suppress("unused")
@Composable
fun ApplicationView(
    fillParent: Boolean = false,
    content: @Composable (ApplicationViewConfig) -> Unit
) {
    var config by remember { mutableStateOf<ApplicationViewConfig?>(null) }

    // launch initialization
    LaunchedEffect(Unit) {
        if (config == null) {
            val systemPrefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches
            val theme = localStorage.getItem(THEME_STORAGE_KEY) ?: if (systemPrefersDark) DARK_THEME else LIGHT_THEME
            val prefersDark = localStorage.getItem(THEME_STORAGE_KEY) == DARK_THEME || systemPrefersDark

            if (prefersDark) {
                document.documentElement?.classList?.add(DARK_THEME)
            }
            config = config?.copy(theme = theme) ?: ApplicationViewConfig(theme)
        }
    }

    DisposableEffect(Unit) {
        val themeChanged: (Event) -> Unit = { e ->
            val ce = e.asDynamic() as CustomEvent
            logInfo("Theme changed: ${ce.detail}")
            val theme = ce.detail as String
            val darkMode = (theme == DARK_THEME)

            if (darkMode) {
                document.documentElement?.classList?.add(DARK_THEME)
            } else {
                document.documentElement?.classList?.remove(DARK_THEME)
            }

            config = config?.copy(theme = theme) ?: ApplicationViewConfig(theme)
        }
        window.addEventListener(THEME_CHANGED_EVENT, themeChanged)

        onDispose { window.removeEventListener(THEME_CHANGED_EVENT, themeChanged) }
    }

    // listen for storage events. These are NOT fired for the container, because the container sets the value, so we
    // fire a custom event and handle that.
    DisposableEffect(Unit) {
        val storageHandler: (Event) -> Unit = { e ->
            val se = e as StorageEvent
            if (se.key == THEME_STORAGE_KEY) {
                val value = se.newValue // could be null if removed
                window.dispatchEvent(CustomEvent(THEME_CHANGED_EVENT, CustomEventInit(detail = value)))
            }
        }
        window.addEventListener(STORAGE_EVENT, storageHandler)

        onDispose { window.removeEventListener(STORAGE_EVENT, storageHandler) }
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            position(Position.Relative)
            if (fillParent) {
                flex(1, 1, 0.px)
                minHeight(0.px)
                // Content that doesn't bound its own height scrolls here rather than growing the
                // document, which would scroll the header and player away with it.
                overflow("auto")
            } else {
                height(100.vh)
            }
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Stretch)
                flex(1, 1)
                flexBasis("auto")
                minHeight(0.px)
                backgroundColor(SXMUI.mainLayoutBackgroundColor.value())
            }
        }) {
            config?.let {
                content(it)
            }
        }
    }
}

/**
 * Installs all application styles
 */
@Composable
fun ApplicationStyles() {
    Style(AppStylesheet)
    Style(EntityViewStyles)
    Style(ServiceViewStyles)
    Style(DialogStyles)
    Style(DetailBoxStyles)
    Style(ButtonStyles)
    Style(Icons)
    Style(PillStyles)
    Style(PaginatorStyles)
    Style(BoxStyles)
    Style(BreadCrumbStyles)
    Style(MessageBoxStyles)
    Style(NotificationStyles)
    Style(SplitSelectionViewStyles)
    Style(TableStyles)
    Style(PaginationStyles)
    Style(CardStyles)
    Style(AccessControlBadgeStyles)
}

/**
 * Save the dark mode setting based on a user preference.
 */
fun updateDarkMode(dark: Boolean) {
    val theme = if (dark) DARK_THEME else LIGHT_THEME
    localStorage.setItem(THEME_STORAGE_KEY, theme)

    // fire a custom theme changed event
    window.dispatchEvent(CustomEvent(THEME_CHANGED_EVENT, CustomEventInit(detail = theme)))
}